import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Separator } from "../components/ui/separator";
import { ArrowLeft, BarChart3, Info, Lightbulb, Play, Sparkles } from "lucide-react";
import CompLookalike, { ResultDisplay } from "../components/CompLookalike";
import { useState, useEffect } from "react";
import myData from '../data/lookalike.json';
import mtaData from '../data/mta.json';
import mmmData from '../data/mmm.json';
import cltvData from '../data/cltv.json';
import vocaData from '../data/vocaAnalytics.json';
import VideoEmbed from "../components/VideoEmbed";
import MmmTabs from "../components/MmmTabs";
import ChannelAttributionChart from "../components/ChannelAttributionChart";
import StackedBarChart from "../components/StackedBarChart";
import FourPieCharts from "../components/FourPieCharts";
import RfmVisualization from "../components/RfmVisualization";
import CustomerCards from "../components/CustomerCards";
import SpendingSummaryPanel from "../components/SpendingSummaryPanel";
import MMMInsights from "../components/MMMInsights";
import VOCAInsights from "../components/VOCAInsights";
import VocaMain from "../components/VocaMain";
import { ECltvChartBgnbd, ECltvChartGgf, ECltvChartSegment, ECltvChartSpend } from "../components/CltvEchart";
import CLTVSummary from "../components/CLTVSummary";
import CustomerInsights from "../components/CustomerInsights";
import CustomerProfiling from "../components/CustomerProfiling";
import ColumnTypePieChart from "../components/CIDR/ColumnTypePieChart";
import ContributingFieldsBarChart from "../components/CIDR/ContributingFieldsBarChart";
import DeduplicationSummaryPieChart from "../components/CIDR/DeduplicationSummaryPieChart";
import DuplicateClusterSizeHistogram from "../components/CIDR/DuplicateClusterSizeHistogram";
import DuplicateSimilarityHistogram from "../components/CIDR/DuplicateSimilarityHistogram";
import HorizontalBarChart from "../components/CIDR/HorizontalBarChart";
import { cards } from "../components/CardGrid";
import ChannelAttributionTreemap from "../components/ChannelAttributionTreemap";
import CustomerBarRaceChart from "../components/CustomerBarRaceChart";
import ChannelAttributionSwitcherChart from "../components/ChannelAttributionSwitcherChart";

const Model = () => {
    const { id, title } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState("tab1");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const tabs = [
        { id: "tab1", label: "Visualization", icon: BarChart3, color: "from-blue-400 to-cyan-500" },
        { id: "tab2", label: "Insights", icon: Lightbulb, color: "from-yellow-400 to-orange-500" },
        // { id: "tab3", label: "Video Demo", icon: Play, color: "from-purple-400 to-pink-500" },
    ].filter(tab => !(id == 3 && tab.label === "Insights"));

    const renderContent = () => {
        switch (activeTab) {
            case "tab1":
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {id == 1 && <CompLookalike />}
                        {id == 2 && <RfmVisualization />}

                        {id == 3 && mtaData && (
                            <div className="space-y-8">
                                <motion.div
                                    className="border border-border/50 rounded-2xl shadow-xl p-6 bg-card/60 backdrop-blur-xl"
                                    whileHover={{ y: -5 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                >
                                    <ChannelAttributionSwitcherChart data={mtaData.results.attribution_summary} />


                                </motion.div>
                                <motion.div
                                    className="border border-border/50 rounded-2xl shadow-xl p-6 bg-card/60 backdrop-blur-xl"
                                    whileHover={{ y: -5 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                >


                                    <ChannelAttributionChart data={mtaData.results.attribution_summary} />
                                </motion.div>
                                <motion.div
                                    className="border border-border/50 rounded-2xl shadow-xl p-6 bg-card/60 backdrop-blur-xl"
                                    whileHover={{ y: -5 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                >
                                    <StackedBarChart data={mtaData.results.attribution_summary} />
                                </motion.div>
                                <motion.div
                                    className="border border-border/50 rounded-2xl shadow-xl p-6 bg-card/60 backdrop-blur-xl"
                                    whileHover={{ y: -5 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                >
                                    <FourPieCharts data={mtaData.results.attribution_summary} />
                                </motion.div>
                            </div>
                        )}

                        {id == 4 && <VocaMain />}

                        {id == 5 && <MmmTabs />}

                        {id == 6 && (
                            <div className="space-y-8">

                                <motion.div
                                    className="border border-border/50 rounded-2xl shadow-xl p-6 bg-card/60 backdrop-blur-xl"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 1 * 0.1 }}
                                    whileHover={{ y: -5 }}
                                >
                                    <h2 className="text-xl font-bold mb-4">Customer Lifetime Value (2000–2025)</h2>
                                    <CustomerBarRaceChart />
                                </motion.div>
                                {[
                                    { component: ECltvChartBgnbd, data: cltvData?.results?.plot_source_data?.bgnbd_validation },
                                    { component: ECltvChartSpend, data: cltvData?.results?.plot_source_data?.cltv_validation },
                                    { component: ECltvChartGgf, data: cltvData?.results?.plot_source_data?.ggf_validation },
                                    { component: ECltvChartSegment, data: cltvData?.results?.plot_source_data?.cltv_segmentation }
                                ].map((item, index) => {
                                    const Component = item.component;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="border border-border/50 rounded-2xl shadow-xl p-6 bg-card/60 backdrop-blur-xl"
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.5, delay: index * 0.1 }}
                                            whileHover={{ y: -5 }}
                                        >
                                            <Component data={item.data} />
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}

                        {id == 7 && (
                            <div className="space-y-8">
                                <iframe
                                    src={`${process.env.PUBLIC_URL}/customer_identity_resolution.html`}
                                    style={{ width: '100%', height: '860px', border: 'none' }}
                                    title="HTML Viewer"
                                />
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                >
                                    <DuplicateSimilarityHistogram />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.1 }}
                                >
                                    <DuplicateClusterSizeHistogram />
                                </motion.div>
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: 0.2 }}
                                >
                                    <DeduplicationSummaryPieChart />
                                </motion.div>
                            </div>
                        )}
                    </motion.div>
                );
            case "tab2":
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        {id == 1 && (
                            <div className="space-y-8">
                                <CustomerInsights />
                                <CustomerProfiling />
                            </div>
                        )}

                        {id == 2 && <CustomerCards />}

                        {id == 4 && (
                            <VOCAInsights
                                analyticsResults={vocaData.analytics}
                                loading={false}
                            />
                        )}

                        {id == 6 && <CLTVSummary />}

                        {id == 5 && (
                            <div className="space-y-8">
                                <motion.div
                                    className="flex flex-col md:flex-row bg-card/60 backdrop-blur-xl shadow-2xl rounded-2xl overflow-hidden border border-border/50"
                                    whileHover={{ scale: 1.02 }}
                                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                >
                                    <div className="md:w-1/3 w-full p-8 flex flex-col justify-evenly bg-gradient-to-br from-accent/10 to-primary/10">
                                        <div className="space-y-6">
                                            <div>
                                                <h2 className="text-lg font-semibold text-foreground mb-2">Top Channel - Google_SEM</h2>
                                                <p className="text-3xl font-bold bg-gradient-to-r from-accent to-primary bg-clip-text mb-2">
                                                    {mmmData?.celery_status?.result?.insights?.top_spending_channels.channels.find(item => item.channel === "Google_SEM").formatted_spend}
                                                </p>
                                                <p className="text-sm text-muted-foreground mb-5 border-b border-border/50 pb-2">
                                                    Percentage: {mmmData?.celery_status?.result?.insights?.top_spending_channels.channels.find(item => item.channel === "Google_SEM").percentage}%
                                                </p>
                                            </div>

                                            <div>
                                                <h2 className="text-lg font-semibold text-foreground mb-2">Total Revenue</h2>
                                                <p className="text-3xl font-bold bg-gradient-to-r from-chart-1 to-chart-2 bg-clip-text mb-2">
                                                    {mmmData?.celery_status?.result?.insights?.total_revenue?.formatted}
                                                </p>
                                                <p className="text-sm text-muted-foreground mb-5 border-b border-border/50 pb-2">
                                                    {mmmData?.celery_status?.result?.insights?.total_revenue?.description}
                                                </p>
                                            </div>

                                            <div>
                                                <h2 className="text-lg font-semibold text-foreground mb-2">Total Spend</h2>
                                                <p className="text-3xl font-bold bg-gradient-to-r from-destructive to-accent bg-clip-text mb-2">
                                                    {mmmData?.celery_status?.result?.insights?.top_spending_channels?.total_spend.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                                                </p>
                                                <p className="text-sm text-muted-foreground border-b border-border/50 pb-2">
                                                    {mmmData?.celery_status?.result?.insights?.top_spending_channels?.description}
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="md:w-2/3 w-full">
                                        <SpendingSummaryPanel data={mmmData?.celery_status?.result?.insights?.top_spending_channels || []} />
                                    </div>
                                </motion.div>

                                <MMMInsights insights={mmmData?.celery_status?.result?.insights || {}} />
                            </div>
                        )}

                        {id == 7 && (
                            <div className="space-y-8">
                                <HorizontalBarChart />
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    <motion.div
                                        whileHover={{ y: -5 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    >
                                        <ContributingFieldsBarChart />
                                    </motion.div>
                                    <motion.div
                                        whileHover={{ y: -5 }}
                                        transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                    >
                                        <ColumnTypePieChart />
                                    </motion.div>
                                </div>
                            </div>
                        )}
                    </motion.div>
                );
            case "tab3":
                return (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            <motion.div
                                className="rounded-2xl overflow-hidden shadow-2xl"
                                whileHover={{ scale: 1.02 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            >
                                <VideoEmbed url={cards.find(card => card.id == id)?.videoUrl} />
                            </motion.div>

                            <Card className="border-0 bg-card/60 backdrop-blur-xl shadow-2xl">
                                <CardHeader>
                                    <CardTitle className="flex items-center space-x-3">
                                        <div className="p-2 bg-gradient-to-r from-purple-400 to-pink-500 rounded-lg">
                                            <Play className="w-6 h-6 text-white" />
                                        </div>
                                        <span>About This Model</span>
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-muted-foreground leading-relaxed">
                                        {cards.find(card => card.id == id)?.description}
                                    </p>
                                </CardContent>
                            </Card>
                        </div>
                    </motion.div>
                );
            default:
                return null;
        }
    };

    const [isOpen, setIsOpen] = useState(false);

    return (
        <div className="min-h-screen bg-background relative overflow-hidden dark:bg-black dark:text-white">
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-accent/10 to-primary/10 rounded-full blur-3xl animate-pulse"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-chart-1/10 to-chart-2/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
            </div>



            {/* Navigation Tabs */}
            <motion.section
                className="px-6 py-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
            >
                <div className="max-w-7xl mx-auto">

                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(-1)}
                        className="rounded-full bg-gray-100 hover:bg-accent/70 hover:text-accent dark:bg-gray-800"
                    >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                    </Button>
                    <label style={{ fontSize: window.innerWidth < 768 ? 20 : 24, marginLeft: 10 }}>{title}</label>


                    <button
                        onClick={() => setIsOpen(true)}
                        className="ml-2 p-1 w-6 h-6 rounded-full  transition"
                        aria-label="Information"
                    >
                        <Info size={20} className="text-blue-500 hover:text-blue-800 dark:text-white" />
                    </button>


                    {
                        isOpen && (
                            <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
                                <div className="bg-white p-6 rounded-lg shadow-lg w-80">
                                    <h2 className="text-lg font-semibold text-black mb-2">About the Model</h2>
                                    <p className="text-gray-600">
                                        {cards.find(card => card.id == id)?.description}
                                    </p>
                                    <button
                                        onClick={() => setIsOpen(false)}
                                        className="mt-4 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        )
                    }


                    <Card className="mt-3 bg-white text-black dark:bg-gray-900 dark:text-white border-0 backdrop-blur-xl shadow-2xl mb-8 overflow-hidden">
                        <CardContent className="p-3">
                            <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:space-x-2 overflow-x-auto">
                                {tabs.map((tab, index) => {
                                    const IconComponent = tab.icon;
                                    const isActive = activeTab === tab.id;
                                    return (
                                        <motion.div
                                            key={tab.id}
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                        >
                                            <Button
                                                variant={isActive ? "gradient" : "ghost"}
                                                size="sm"
                                                onClick={() => setActiveTab(tab.id)}
                                                className={`rounded-xl flex items-center space-x-2 relative overflow-hidden group 
                ${isActive ? 'shadow-lg' : 'hover:bg-accent/10'} 
                text-gray-900 dark:text-white  hover:text-black dark:hover:text-white`}
                                            >
                                                <motion.div
                                                    animate={isActive ? { rotate: 360 } : { rotate: 0 }}
                                                    transition={{ duration: 0.5 }}
                                                >
                                                    <IconComponent className="w-4 h-4" />
                                                </motion.div>
                                                <span>{tab.label}</span>


                                                {
                                                    isActive && (
                                                        <motion.div
                                                            className="absolute inset-0 bg-gradient-to-r from-black/5 to-transparent dark:from-white/10 rounded-xl"
                                                            layoutId="activeTabBg"
                                                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                                        />
                                                    )
                                                }
                                            </Button>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </CardContent>
                    </Card>

                </div >
            </motion.section >

            {/* Tab Content */}
            < motion.section
                className="px-1 pb-24"
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
            >
                <div className="max-w-7xl mx-auto">
                    {renderContent()}
                </div>
            </motion.section >
        </div >
    );
};

export default Model;